import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { adminService } from 'core/services/adminService'
import { formatNumbers } from 'core/helpers/format-numbers'
import { routes } from 'core/routes/paths'

import c from './assets/sass/battles-admin.module.scss'

export const BattlesAdmin = () => {
    const [view, setView] = useState('stats')

    return (
        <div className={c.component}>
            <div className={c.tabs}>
                <button
                    className={view === 'stats' ? c.tabActive : c.tab}
                    onClick={() => setView('stats')}
                >
                    📊 Статистика
                </button>
                <button
                    className={view === 'players' ? c.tabActive : c.tab}
                    onClick={() => setView('players')}
                >
                    🏆 Игроки
                </button>
                <button
                    className={view === 'bots' ? c.tabActive : c.tab}
                    onClick={() => setView('bots')}
                >
                    🤖 Боты
                </button>
                <button
                    className={view === 'config' ? c.tabActive : c.tab}
                    onClick={() => setView('config')}
                >
                    ⚙️ Настройки
                </button>
                <button
                    className={view === 'history' ? c.tabActive : c.tab}
                    onClick={() => setView('history')}
                >
                    📋 История
                </button>
                <button
                    className={view === 'abuse' ? c.tabActive : c.tab}
                    onClick={() => setView('abuse')}
                >
                    🚨 Абуз
                </button>
            </div>

            {view === 'stats' && <StatsView />}
            {view === 'players' && <PlayersView />}
            {view === 'bots' && <BotsView />}
            {view === 'config' && <ConfigView />}
            {view === 'history' && <HistoryView />}
            {view === 'abuse' && <AbuseView />}
        </div>
    )
}

const StatsView = () => {
    const [stats, setStats] = useState(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        adminService.getBattlesStats()
            .then(data => setStats(data.stats))
            .catch(console.error)
            .finally(() => setLoading(false))
    }, [])

    if (loading) return <div className={c.loading}>Загрузка...</div>
    if (!stats) return <div className={c.loading}>Ошибка загрузки</div>

    const houseProfit = parseInt(stats.houseProfit)

    return (
        <>
            <div className={c.statsSection}>
                <h3 className={c.sectionTitle}>💰 За всё время</h3>
                <div className={c.statsGrid}>
                    <div className={c.statsCard}>
                        <span className={c.statsLabel}>Всего батлов</span>
                        <span className={c.statsValue}>{formatNumbers(stats.totalGames)}</span>
                    </div>
                    <div className={c.statsCard}>
                        <span className={c.statsLabel}>Завершено</span>
                        <span className={c.statsValueGreen}>{formatNumbers(stats.finished)}</span>
                    </div>
                    <div className={c.statsCard}>
                        <span className={c.statsLabel}>Отменено</span>
                        <span className={c.statsValueRed}>{formatNumbers(stats.cancelled)}</span>
                    </div>
                    <div className={c.statsCard}>
                        <span className={c.statsLabel}>Оборот</span>
                        <span className={c.statsValue}>{formatNumbers(parseInt(stats.totalWagered))}</span>
                    </div>
                    <div className={c.statsCard}>
                        <span className={c.statsLabel}>Побед игроков</span>
                        <span className={c.statsValueGreen}>{formatNumbers(stats.playerWins)}</span>
                    </div>
                    <div className={c.statsCard}>
                        <span className={c.statsLabel}>Побед ботов</span>
                        <span className={c.statsValueRed}>{formatNumbers(stats.botWins)}</span>
                    </div>
                    <div className={c.statsCard}>
                        <span className={c.statsLabel}>Выплачено игрокам</span>
                        <span className={c.statsValue}>{formatNumbers(parseInt(stats.playerWinnings))}</span>
                    </div>
                    <div className={c.statsCard}>
                        <span className={c.statsLabel}>Выиграно ботами</span>
                        <span className={c.statsValue}>{formatNumbers(parseInt(stats.botWinnings))}</span>
                    </div>
                    <div className={c.statsCard}>
                        <span className={c.statsLabel}>Профит приложения</span>
                        <span className={houseProfit >= 0 ? c.statsValueGreen : c.statsValueRed}>
                            {houseProfit >= 0 ? '+' : ''}{formatNumbers(houseProfit)}
                        </span>
                    </div>
                </div>
            </div>

            <div className={c.statsSection}>
                <h3 className={c.sectionTitle}>📅 Сегодня</h3>
                <div className={c.statsGrid}>
                    <div className={c.statsCard}>
                        <span className={c.statsLabel}>Батлов</span>
                        <span className={c.statsValue}>{formatNumbers(stats.today.games)}</span>
                    </div>
                    <div className={c.statsCard}>
                        <span className={c.statsLabel}>Игроков</span>
                        <span className={c.statsValue}>{formatNumbers(stats.today.uniquePlayers)}</span>
                    </div>
                    <div className={c.statsCard}>
                        <span className={c.statsLabel}>Оборот</span>
                        <span className={c.statsValue}>{formatNumbers(parseInt(stats.today.wagered))}</span>
                    </div>
                </div>
            </div>
        </>
    )
}

const PERIODS = [
    { id: '24h', label: '24 часа' },
    { id: '7d', label: '7 дней' },
    { id: '30d', label: '30 дней' },
]

const PlayersView = () => {
    const [period, setPeriod] = useState('24h')
    const [players, setPlayers] = useState([])
    const [loading, setLoading] = useState(true)
    const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 })
    const navigate = useNavigate()

    const loadPlayers = useCallback(async (page) => {
        setLoading(true)
        try {
            const data = await adminService.getBattlesTopPlayers(period, page, 50)
            setPlayers(data.players || [])
            if (data.pagination) setPagination(data.pagination)
        } catch (e) {
            console.error('Failed to load top players:', e)
        } finally {
            setLoading(false)
        }
    }, [period])

    useEffect(() => {
        setPagination(p => ({ ...p, page: 1 }))
        loadPlayers(1)
    }, [loadPlayers])

    const handleUserClick = (userId) => {
        navigate(`${routes.adminUsers}?userId=${userId}`)
    }

    const offset = (pagination.page - 1) * pagination.limit

    return (
        <>
            <div className={c.filterTabs}>
                {PERIODS.map(p => (
                    <button
                        key={p.id}
                        className={period === p.id ? c.filterTabActive : c.filterTab}
                        onClick={() => setPeriod(p.id)}
                    >
                        {p.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className={c.loading}>Загрузка...</div>
            ) : (
                <div className={c.statsSection}>
                    <h3 className={c.sectionTitle}>🏆 Топ игроков — {PERIODS.find(p => p.id === period)?.label}</h3>
                    <div className={c.topList}>
                        {players.map((p, i) => {
                            const profit = parseInt(p.netProfit)
                            return (
                                <div
                                    key={p.userId}
                                    className={c.topItem}
                                    onClick={() => handleUserClick(p.userId)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div className={c.topName}>
                                        <span>{offset + i + 1}. </span>
                                        <span className={c.topUserLink}>{p.name}</span>
                                        <span className={c.topMeta}>
                                            {p.games} игр · {p.wins}W / {p.losses}L
                                        </span>
                                    </div>
                                    <span className={profit >= 0 ? c.topProfit : c.topProfitNeg}>
                                        {profit >= 0 ? '+' : ''}{formatNumbers(profit)}
                                    </span>
                                </div>
                            )
                        })}
                        {players.length === 0 && (
                            <div className={c.emptyState}>Нет данных за этот период</div>
                        )}
                    </div>
                    {pagination.pages > 1 && (
                        <div className={c.pagination}>
                            <button
                                onClick={() => { const p = pagination.page - 1; setPagination(prev => ({ ...prev, page: p })); loadPlayers(p) }}
                                disabled={pagination.page === 1}
                            >
                                ← Назад
                            </button>
                            <span>Страница {pagination.page} из {pagination.pages}</span>
                            <button
                                onClick={() => { const p = pagination.page + 1; setPagination(prev => ({ ...prev, page: p })); loadPlayers(p) }}
                                disabled={pagination.page >= pagination.pages}
                            >
                                Вперед →
                            </button>
                        </div>
                    )}
                </div>
            )}
        </>
    )
}

const BotsView = () => {
    const [bots, setBots] = useState([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        adminService.getBattlesBots()
            .then(data => setBots(data.bots || []))
            .catch(console.error)
            .finally(() => setLoading(false))
    }, [])

    if (loading) return <div className={c.loading}>Загрузка...</div>

    return (
        <div className={c.statsSection}>
            <h3 className={c.sectionTitle}>🤖 Пул ботов</h3>
            {bots.map(bot => (
                <div key={bot.id} className={c.botCard}>
                    <img
                        src={bot.photoUrl || '/images/avatar-02.png'}
                        alt={bot.username}
                        className={c.botAvatar}
                    />
                    <div className={c.botInfo}>
                        <div className={c.botName}>
                            {bot.username}
                            <span className={c.botTid}> · TID: {bot.telegramId}</span>
                        </div>
                        <div className={c.botStatsRow}>
                            <span>Игр: <span className={c.botStatVal}>{bot.stats.totalGames}</span></span>
                            <span>Побед: <span className={c.botStatGreen}>{bot.stats.wins}</span></span>
                            <span>Проигрышей: <span className={c.botStatRed}>{bot.stats.losses}</span></span>
                            <span>Винрейт: <span className={c.botStatVal}>{bot.stats.winrate}%</span></span>
                        </div>
                        <div className={c.botStatsRow}>
                            <span>Оборот: <span className={c.botStatVal}>{formatNumbers(parseInt(bot.stats.totalWagered))}</span></span>
                            <span>Выиграно: <span className={c.botStatGreen}>{formatNumbers(parseInt(bot.stats.totalWon))}</span></span>
                            <span>Баланс: <span className={c.botStatVal}>{formatNumbers(parseInt(bot.balance))}</span></span>
                        </div>
                    </div>
                </div>
            ))}
            {bots.length === 0 && (
                <div className={c.emptyState}>Боты не найдены</div>
            )}
        </div>
    )
}

const Tooltip = ({ text }) => (
    <span className={c.tooltip}>
        <span className={c.tooltipIcon}>?</span>
        <span className={c.tooltipText}>{text}</span>
    </span>
)

const CONFIG_FIELDS = [
    { key: 'baseWinChance', label: 'Базовый шанс игрока', step: '0.01',
      hint: 'Вероятность победы игрока в каждом батле до модификаторов. 0.49 = 49%. Остальное получает бот.' },
    { key: 'botAdvantage', label: 'Доп. преимущество бота', step: '0.01',
      hint: 'Дополнительная вероятность в пользу бота поверх базового шанса. 0 = не используется (уже заложено в базовый шанс).' },
    { key: 'maxLossStreak', label: 'Макс. серия проигрышей',
      hint: 'Максимальная длина серии проигрышей для расчёта буста. После этого значения буст перестаёт расти.' },
    { key: 'maxWinStreak', label: 'Макс. серия побед',
      hint: 'Максимальная длина серии побед для расчёта штрафа. После этого значения штраф перестаёт расти.' },
    { key: 'maxStreakBoost', label: 'Макс. буст при проигрышах', step: '0.01',
      hint: 'Максимальное увеличение шанса игрока при серии проигрышей. 0.08 = +8% к шансу.' },
    { key: 'maxStreakPenalty', label: 'Макс. штраф при победах', step: '0.01',
      hint: 'Максимальное уменьшение шанса игрока при серии побед. 0.06 = -6% от шанса.' },
    { key: 'minChance', label: 'Мин. шанс игрока', step: '0.01',
      hint: 'Нижний порог шанса игрока после всех модификаторов. Шанс не опустится ниже этого значения.' },
    { key: 'maxChance', label: 'Макс. шанс игрока', step: '0.01',
      hint: 'Верхний порог шанса игрока после всех модификаторов. Шанс не поднимется выше этого значения.' },
    { key: 'sessionTimeHours', label: 'Время сессии (часов)',
      hint: 'Длительность игровой сессии. По истечении — профит сессии сбрасывается и начинается заново.' },
    { key: 'maxSessionProfit', label: 'Макс. профит за сессию',
      hint: 'Максимальный чистый выигрыш игрока за одну сессию (в монетах). При достижении — шанс начинает снижаться.' },
    { key: 'maxSessionLoss', label: 'Макс. убыток за сессию',
      hint: 'Максимальный убыток игрока за сессию (отрицательное число). При достижении — шанс начинает повышаться.' },
    { key: 'commissionEnabled', label: 'Комиссия включена', type: 'toggle',
      hint: 'Включить/выключить комиссию с батлов. При игре с ботами обычно отключена.' },
    { key: 'commissionRate', label: 'Размер комиссии', step: '0.01',
      hint: 'Процент комиссии с выигрыша. 0.05 = 5%. Применяется только если комиссия включена.' },
    { key: 'botSearchDelayMin', label: 'Мин. задержка поиска (мс)',
      hint: 'Минимальное время "поиска" соперника перед подключением бота (в миллисекундах). 5000 = 5 секунд.' },
    { key: 'botSearchDelayMax', label: 'Макс. задержка поиска (мс)',
      hint: 'Максимальное время "поиска" соперника (в миллисекундах). 10000 = 10 секунд. Реальная задержка — случайное число между мин и макс.' },
    { key: 'isActive', label: 'Батлы активны', type: 'toggle',
      hint: 'Глобальный переключатель. Если выключено — игроки не смогут создавать новые батлы.' },
]

const ConfigView = () => {
    const [draft, setDraft] = useState(null)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [saved, setSaved] = useState(false)

    useEffect(() => {
        adminService.getBattlesConfig()
            .then(data => setDraft(data.config))
            .catch(console.error)
            .finally(() => setLoading(false))
    }, [])

    const handleSave = async () => {
        setSaving(true)
        setSaved(false)
        try {
            const data = await adminService.updateBattlesConfig(draft)
            setDraft(data.config)
            setSaved(true)
            setTimeout(() => setSaved(false), 2000)
        } catch (e) {
            console.error('Save config error:', e)
            alert('Ошибка сохранения: ' + (e.response?.data?.error || e.message))
        } finally {
            setSaving(false)
        }
    }

    const update = (key, value) => {
        setDraft(prev => ({ ...prev, [key]: parseFloat(value) || value }))
    }

    const toggleField = (key) => {
        setDraft(prev => ({ ...prev, [key]: !prev[key] }))
    }

    if (loading) return <div className={c.loading}>Загрузка...</div>
    if (!draft) return <div className={c.loading}>Ошибка загрузки</div>

    return (
        <div className={c.configSection}>
            <h3 className={c.sectionTitle}>⚙️ Параметры батлов</h3>
            <div style={{ fontSize: 11, color: 'rgba(76, 175, 80, 0.7)', marginBottom: 12 }}>
                ⚡ Все настройки применяются мгновенно без перезапуска сервера
            </div>
            <div className={c.configGrid}>
                {CONFIG_FIELDS.map(f => (
                    <div key={f.key} className={c.configRow}>
                        <span className={c.configLabel}>
                            {f.label}
                            {f.hint && <Tooltip text={f.hint} />}
                        </span>
                        {f.type === 'toggle' ? (
                            <button
                                className={c.configInput}
                                style={{
                                    width: 'auto',
                                    cursor: 'pointer',
                                    background: draft[f.key] ? 'rgba(76, 175, 80, 0.3)' : 'rgba(244, 67, 54, 0.2)',
                                    color: draft[f.key] ? '#4CAF50' : '#f44336',
                                    textAlign: 'center',
                                    border: `1px solid ${draft[f.key] ? 'rgba(76, 175, 80, 0.4)' : 'rgba(244, 67, 54, 0.3)'}`
                                }}
                                onClick={() => toggleField(f.key)}
                            >
                                {draft[f.key] ? 'ВКЛ' : 'ВЫКЛ'}
                            </button>
                        ) : (
                            <input
                                type="number"
                                step={f.step || '1'}
                                className={c.configInput}
                                value={draft[f.key] ?? ''}
                                onChange={(e) => update(f.key, e.target.value)}
                            />
                        )}
                    </div>
                ))}
            </div>
            <button className={c.saveBtn} onClick={handleSave} disabled={saving}>
                {saving ? 'Сохранение...' : saved ? '✅ Сохранено!' : 'Сохранить настройки'}
            </button>
        </div>
    )
}

const STATUS_LABELS = {
    FINISHED: 'Завершён',
    WAITING: 'Ожидание',
    ACTIVE: 'Активен',
    CANCELLED: 'Отменён',
}

const STATUS_CLASSES = {
    FINISHED: cls => cls.statusFinished,
    WAITING: cls => cls.statusWaiting,
    ACTIVE: cls => cls.statusActive,
    CANCELLED: cls => cls.statusCancelled,
}

const formatDate = (dateString) => {
    if (!dateString) return '—'
    return new Date(dateString).toLocaleString('ru-RU', {
        day: '2-digit', month: '2-digit',
        hour: '2-digit', minute: '2-digit',
    })
}

const FILTERS = [
    { id: '', label: 'Все' },
    { id: 'FINISHED', label: '✅ Завершены' },
    { id: 'WAITING', label: '⏳ Ожидание' },
    { id: 'ACTIVE', label: '🟡 Активные' },
    { id: 'CANCELLED', label: '🚫 Отменены' },
]

const HistoryView = () => {
    const [games, setGames] = useState([])
    const [loading, setLoading] = useState(true)
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 })
    const [statusFilter, setStatusFilter] = useState('')
    const navigate = useNavigate()

    const loadGames = useCallback(async (page, status) => {
        try {
            setLoading(true)
            const params = { page, limit: pagination.limit }
            if (status) params.status = status
            const data = await adminService.getBattlesGames(params)
            setGames(data.games || [])
            setPagination(data.pagination)
        } catch (e) {
            console.error('Failed to load games:', e)
        } finally {
            setLoading(false)
        }
    }, [pagination.limit])

    useEffect(() => {
        loadGames(pagination.page, statusFilter)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.page, statusFilter])

    const handleFilterChange = (status) => {
        setStatusFilter(status)
        setPagination(p => ({ ...p, page: 1 }))
    }

    const handleUserClick = (userId) => {
        navigate(`${routes.adminUsers}?userId=${userId}`)
    }

    return (
        <>
            <div className={c.filterTabs}>
                {FILTERS.map(f => (
                    <button
                        key={f.id}
                        className={statusFilter === f.id ? c.filterTabActive : c.filterTab}
                        onClick={() => handleFilterChange(f.id)}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            {loading && games.length === 0 ? (
                <div className={c.loading}>Загрузка...</div>
            ) : (
                <>
                    <div className={c.list}>
                        {games.map(game => {
                            const statusCls = (STATUS_CLASSES[game.status] || STATUS_CLASSES.CANCELLED)(c)
                            const p1 = game.player1
                            const p2 = game.player2
                            return (
                                <div key={game.id} className={c.item}>
                                    <span
                                        className={c.itemUserLink}
                                        onClick={() => !p1.isBot && handleUserClick(p1.id)}
                                        style={{ cursor: p1.isBot ? 'default' : 'pointer' }}
                                    >
                                        {p1.name}
                                        {p1.isBot && <span className={c.metaBot}>БОТ</span>}
                                    </span>
                                    <span className={`${c.itemStatus} ${statusCls}`}>
                                        {STATUS_LABELS[game.status] || game.status}
                                    </span>
                                    <div className={c.itemMeta}>
                                        <span>
                                            Ставка: <span className={c.metaVal}>{formatNumbers(parseInt(game.betAmount))}</span>
                                        </span>
                                        {p2 && (
                                            <span>
                                                VS: <span
                                                    className={c.metaVal}
                                                    onClick={() => !p2.isBot && handleUserClick(p2.id)}
                                                    style={{ cursor: p2.isBot ? 'default' : 'pointer', color: p2.isBot ? '#FF8800' : '#4CAF50' }}
                                                >
                                                    {p2.name}
                                                </span>
                                                {p2.isBot && <span className={c.metaBot}>БОТ</span>}
                                            </span>
                                        )}
                                        {game.winnerId && (
                                            <span>
                                                Победитель: <span className={c.metaVal} style={{ color: '#4CAF50' }}>
                                                    {game.winnerId === p1.id ? p1.name : p2?.name || '—'}
                                                </span>
                                            </span>
                                        )}
                                        <span className={c.metaDate}>{formatDate(game.createdAt)}</span>
                                    </div>
                                </div>
                            )
                        })}
                        {games.length === 0 && (
                            <div className={c.emptyState}>Нет батлов</div>
                        )}
                    </div>

                    {pagination.pages > 1 && (
                        <div className={c.pagination}>
                            <button
                                onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                                disabled={pagination.page === 1}
                            >
                                ← Назад
                            </button>
                            <span>Страница {pagination.page} из {pagination.pages}</span>
                            <button
                                onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                                disabled={pagination.page >= pagination.pages}
                            >
                                Вперед →
                            </button>
                        </div>
                    )}
                </>
            )}
        </>
    )
}

const AbuseView = () => {
    const [period, setPeriod] = useState('24h')
    const [suspects, setSuspects] = useState([])
    const [loading, setLoading] = useState(true)
    const navigate = useNavigate()

    const loadData = useCallback(async () => {
        setLoading(true)
        try {
            const data = await adminService.getBattlesAbuse(period)
            setSuspects(data.suspects || [])
        } catch (e) {
            console.error('Failed to load abuse report:', e)
        } finally {
            setLoading(false)
        }
    }, [period])

    useEffect(() => { loadData() }, [loadData])

    const handleUserClick = (userId) => {
        navigate(`${routes.adminUsers}?userId=${userId}`)
    }

    return (
        <>
            <div className={c.filterTabs}>
                {PERIODS.map(p => (
                    <button
                        key={p.id}
                        className={period === p.id ? c.filterTabActive : c.filterTab}
                        onClick={() => setPeriod(p.id)}
                    >
                        {p.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className={c.loading}>Загрузка...</div>
            ) : (
                <div className={c.statsSection}>
                    <h3 className={c.sectionTitle}>🚨 Подозрительные игроки — {PERIODS.find(p => p.id === period)?.label}</h3>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 12 }}>
                        Критерии: от 10 игр + винрейт ≥70% или профит ≥5000 монет
                    </div>
                    <div className={c.topList}>
                        {suspects.map((s, i) => (
                            <div
                                key={s.userId}
                                className={c.abuseItem}
                                onClick={() => handleUserClick(s.userId)}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className={c.abuseName}>
                                    <span>{i + 1}. </span>
                                    <span className={c.topUserLink}>{s.name}</span>
                                    <span className={c.abuseMeta}>
                                        {s.games} игр · {s.wins}W / {s.losses}L · профит: {formatNumbers(parseInt(s.netProfit))}
                                    </span>
                                </div>
                                <span className={c.abuseWinrate}>
                                    {s.winrate}%
                                </span>
                            </div>
                        ))}
                        {suspects.length === 0 && (
                            <div className={c.emptyState}>Подозрительных игроков не обнаружено</div>
                        )}
                    </div>
                </div>
            )}
        </>
    )
}
